import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import set from 'lodash/set';
import keys from 'lodash/keys';
import map from 'lodash/map';
import noop from 'lodash/noop';
import reduce from 'lodash/reduce';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { AddNewChatModal } from 'src/presentation/components/pages/chat/components/NewChatModal';
import analytics from '@ad-scripts/lib/analytics';
import { Button } from 'src/presentation/components/agent_desktop_components';
import EmbedModal, { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import endpointsResource from 'src/actions/endpoints/endpointsResource';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import {
  getValueFromURLParameters,
  getValuesFromStringQuery,
  removeSearchOptionFromStringQuery,
} from 'src/presentation/components/pages/shared/utils/Utils';
import GoToChannelPageNotificationModal from './components/GoToChannelPageNotificationModal';
import {
  INIT_CHAT_SECTION_DESCRIPTION,
  LOAD_CHAT_SECTION_DESCRIPTION,
  CONSUMER_NO_REPLY,
  DEPCRATED_NO_REPLY,
} from 'src/presentation/components/pages/chat/chatConstants';
import organizationResource from 'src/actions/organization/organizationResource';
import OverflowMenu, {
  OverflowMenuExternalLink,
  OverflowMenuItem,
  OverflowMenuLink,
  usePopoverMenu,
} from 'src/presentation/components/pages/shared/OverflowMenu';
import { RootState } from 'src/actions/configuration/rootReducer';
import { Section } from 'src/presentation/components/forms/Layout';
import ChatEditor from 'src/presentation/components/pages/chat/ChatEditor';
import Snippet from 'src/presentation/components/pages/shared/Snippet';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import { translate } from 'src/presentation/components/pages/chat/helpers/chatUtils';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import { useEndpointsWithInboxesAndAgent } from '../shared/hooks/useEndpointsWithInboxesAndAgent';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import useChatRocketAccount from 'src/presentation/components/pages/chat/hooks/useChatRocketAccount';
import { CHAT_BRAND } from 'src/constants/branding';

const columnConfigOld = [
  { attr: 'address', label: `${CHAT_BRAND.name} ID` },
  { attr: 'inboxes', label: 'Inboxes' },
];

const columnConfig = [
  { attr: 'address', label: `${CHAT_BRAND.name} ID` },
  { attr: 'inboxes', label: 'Inboxes' },
  { attr: 'updatedByString', label: 'Last Edited By' },
  { attr: 'updatedAtString', label: 'Last Edited On' },
];

const StatusLoading = 'loading';

export interface ChatContextValue {
  accountId: string;
  afterSave?: Function;
}

export const ChatContext = createContext<ChatContextValue>({
  accountId: '',
  afterSave: noop,
});

const description = (
  <span>
    Customize a {CHAT_BRAND.name} by changing quick actions, placement, fonts, colors. Please note that{' '}
    {CHAT_BRAND.plural} cannot be removed once they are created. Learn more about managing {CHAT_BRAND.name} in our Help
    Docs. <ExternalLink data-aid="learn-about-chat" to="https://help.gladly.com/docs/create-and-configure-chat" />.
  </span>
);

const ChatConfigurations: TablePageRender = ({ root }) => {
  const { endpoints, loadingState, endpointsErrors } = useEndpointsWithInboxesAndAgent();
  const accountInformation = useChatRocketAccount();

  const orgId = useSelector((state: RootState) => state.auth.orgId);
  const { value: organization } = useDocumentResource(organizationResource, {
    fetch: true,
    params: { orgId },
  });
  const [isGoToChannelPageModalVisible, setIsGoToChannelPageModalVisible] = useState(false);
  const [newChatAddress, setNewChatAddress] = useState('');

  const [configurations, setConfigurations] = useState([]);

  const isFeatureEnabled = useIsFeatureEnabled();
  const isMobileSDKAvailable = isFeatureEnabled('sidekickMobileSDK');

  useEffect(() => {
    const filteredList = filter(endpoints, value => value.type === 'CHAT');
    setConfigurations(fixOfficeHours(translate(filteredList)));
    setConfigurations(fixNoReplyMessage(translate(filteredList)));
  }, [endpoints]);

  const fixOfficeHours = (endpoints): any => {
    // old Chat configuration has null value for days property that are closed but should not be that property at all.
    // e.i. {monday:[{stop: "23:59", start: "00:01"}],thursday:null} should be {{monday:[{stop: "23:59", start: "00:01"}]}
    return map(endpoints, endpoint => {
      const officeHours = reduce(
        endpoint.configuration.officeHours,
        (acc, officeHours, key) => ({
          ...acc,
          ...(officeHours !== null ? { [key]: officeHours } : {}),
        }),
        {}
      );

      return { ...endpoint, configuration: { ...endpoint.configuration, officeHours } };
    });
  };

  // NoReply message is deprecated. This func serves as a temporary work around to use the deprecated noReply message only if the consumer no reply message does not exist yet. After the admin saves the Chat config, the consumer no reply message will be populated.
  const fixNoReplyMessage = (endpoints): any => {
    return map(endpoints, endpoint => {
      const consumerNoReplyMsg = get(endpoint, CONSUMER_NO_REPLY.path, '');
      const noReplyMsg = get(endpoint, DEPCRATED_NO_REPLY.path, '');
      if (!consumerNoReplyMsg && noReplyMsg) {
        set(endpoint, CONSUMER_NO_REPLY.path, noReplyMsg);
      }
      return endpoint;
    });
  };

  function filterFunc(items, searchString) {
    if (!searchString) {
      return items;
    }

    const desiredAudiences = getValuesFromStringQuery(searchString, 'audiences');
    const cleanSearch = removeSearchOptionFromStringQuery(searchString, 'audiences');

    return filter(items, item => {
      const hasDesiredAudience = !desiredAudiences.length || desiredAudiences.includes(item.configuration.audienceId);
      return hasDesiredAudience && item.address.toLowerCase().indexOf(cleanSearch.toLowerCase()) > -1;
    });
  }

  const [isVisibleEmbedModal, setVisibleEmbedModal] = useState<boolean>();
  const openEmbedModal = function () {
    setVisibleEmbedModal(true);
  };
  const closeEmbedModal = function () {
    setVisibleEmbedModal(false);
  };

  const [isVisibleCreateNewChatModal, setIsVisibleCreateNewChatModal] = useState<boolean>();
  const openCreateNewChatModal = function () {
    setIsVisibleCreateNewChatModal(true);
  };
  const closeCreateNewChatModal = function () {
    setIsVisibleCreateNewChatModal(false);
  };

  const [title, setTitle] = useState<string>();
  const [content, setContent] = useState<ReactNode>();

  const handleOpenEmbedModal = item => {
    setTitle(composeTitle(item));
    setContent(composeContent(item));

    openEmbedModal();
  };

  const actions = (
    <Button
      data-aid={'create-new-chat'}
      type={Button.Types.PRIMARY}
      onClick={() => {
        analytics.track('Admin - Create new Sidekick');
        openCreateNewChatModal();
      }}
    >
      Create {CHAT_BRAND.name}
    </Button>
  );

  const renderRowActions = ({ item }) => {
    return (
      <RowActions
        root={root}
        item={item}
        openEmbedModal={handleOpenEmbedModal}
        isMobileSDKAvailable={isMobileSDKAvailable}
      />
    );
  };

  const afterSave = useCallback(newChat => {
    setIsGoToChannelPageModalVisible(true);
    setNewChatAddress(newChat.address);
  }, []);

  const closeNotification = useCallback(() => {
    setIsGoToChannelPageModalVisible(false);
  }, []);

  const contextValue = useMemo(() => {
    if (!accountInformation) {
      return { accountId: '', afterSave };
    }

    return { accountId: accountInformation.accountId, afterSave };
  }, [accountInformation, afterSave]);

  const audienceParam = getValueFromURLParameters('audiences');
  const initialSearchString = audienceParam ? `audiences:${audienceParam}` : '';

  return (
    <>
      <ChatContext.Provider value={contextValue}>
        <TablePage
          actions={actions}
          columnConfig={isMobileSDKAvailable ? columnConfig : columnConfigOld}
          description={description}
          editor={ChatEditor}
          errors={endpointsErrors}
          filterFunc={filterFunc}
          initialSearchString={initialSearchString}
          isLoading={loadingState === StatusLoading}
          items={configurations}
          name={CHAT_BRAND.name}
          resetErrors={endpointsResource.actions.resetErrors}
          root={root}
          renderRowActions={renderRowActions}
          data-aid="admin-chat-configuration-page"
          modalTopPadding={37}
          preventCloseOnEscape={true}
        />
      </ChatContext.Provider>
      {isVisibleEmbedModal && (
        <StyledEmbedModal title={title} content={content} onCancel={closeEmbedModal} modalSize={ModalSize.LARGE} />
      )}
      {isVisibleCreateNewChatModal && (
        <AddNewChatModal errors={endpointsErrors} onCancel={closeCreateNewChatModal} organization={organization} />
      )}
      {isGoToChannelPageModalVisible && (
        <GoToChannelPageNotificationModal name={newChatAddress} onCancel={closeNotification} />
      )}
    </>
  );
};

const composeTitle = item => {
  return `Embed ${item.address} ${CHAT_BRAND.name}`;
};

const Footer = () => {
  return (
    <span style={{ textAlign: 'center' }}>
      For more information about embed, head to
      <br />
      <a rel="noopener noreferrer" target="_blank" href="https://developer.gladly.com/chat/">
        https://developer.gladly.com/chat/
      </a>
    </span>
  );
};

const getEnv = (): string => {
  const hostname = window.location.hostname;
  return /^(.+\.)?gladly.com$/.test(hostname)
    ? 'PROD'
    : /^us-master.gladly.qa$/.test(hostname)
    ? 'MASTER'
    : /^us-staging.gladly.qa$/.test(hostname)
    ? 'GLADLY-STAGING'
    : /^(.+\.)?gladly.qa$/.test(hostname)
    ? 'STAGING' // This is for UAT
    : 'LOCAL';
};

const getPreviewUrl = (address: string): string => {
  return `https://sagansystems.github.io/chat-sdk/chat-preview?environment=${getEnv()}&id=${address}&domain=${get(
    window,
    'location.origin',
    ''
  )}`;
};

const composeContent = item => {
  const target = getEnv();
  const appId = item.address;

  const snippetSdk = `<script>
  !function(c,n,r,t){if(!c[r]){var i,d,p=[];d="PROD"!==t&&t?"STAGING"===t?"https://cdn.gladly.qa/gladly/chat-sdk/widget.js":t:"https://cdn.gladly.com/chat-sdk/widget.js",c[r]={init:function(){i=arguments;var e={then:function(t){return p.push({type:"t",next:t}),e},catch:function(t){return p.push({type:"c",next:t}),e}};return e}},c.__onHelpAppHostReady__=function(t){if(delete c.__onHelpAppHostReady__,(c[r]=t).loaderCdn=d,i)for(var e=t.init.apply(t,i),n=0;n<p.length;n++){var a=p[n];e="t"===a.type?e.then(a.next):e.catch(a.next)}},function(){try{var t=n.getElementsByTagName("script")[0],e=n.createElement("script");e.async=!0,e.src=d+"?q="+(new Date).getTime(),t.parentNode.insertBefore(e,t)}catch(t){}}()}}
  (window,document,'Gladly','${target}')
</script>`;

  const snippetSetup = `<script>
  window.gladlyConfig = {
    appId: '${appId}'
  };
</script>`;

  return (
    <Section>
      <Section header={`Load ${CHAT_BRAND.name}`} description={LOAD_CHAT_SECTION_DESCRIPTION}>
        <Snippet text={snippetSdk} dataAid="embed.load.script" />
      </Section>
      <Section header={`Initialize ${CHAT_BRAND.name}`} description={INIT_CHAT_SECTION_DESCRIPTION}>
        <Snippet text={snippetSetup} dataAid="embed.initialize.script" />
      </Section>
      <Footer />
    </Section>
  );
};

const renderPreviewLink = item => {
  const configDisplay = get(item, 'configuration.display');

  if (configDisplay === 'DISABLED') {
    return null;
  }

  const previewUrl = getPreviewUrl(item.address);

  return (
    <OverflowMenuItem id="preview">
      <OverflowMenuExternalLink
        href={previewUrl}
        target="_blank"
        onClick={() => {
          analytics.track('Admin - Sidekick Preview Viewed', { sidekickId: item.address });
          return true;
        }}
      >
        View Preview
      </OverflowMenuExternalLink>
    </OverflowMenuItem>
  );
};

function renderConfigureChat(root, item, isMobileSDKAvailable) {
  const pushNotificationAccountIds = get(item, 'configuration.pushNotificationAccountIds', {});

  if (!isMobileSDKAvailable && keys(pushNotificationAccountIds).length > 0) {
    return null;
  }

  return (
    <OverflowMenuItem id="configure">
      <OverflowMenuLink to={`${root}/${item.id}`}>Configure</OverflowMenuLink>
    </OverflowMenuItem>
  );
}

const RowActions = ({ root, item, openEmbedModal, isMobileSDKAvailable }) => {
  const { onClose, ...popoverMenuProps } = usePopoverMenu();

  return (
    <OverflowMenu id={item.id} onClose={onClose} {...popoverMenuProps}>
      <OverflowMenuItem id="throttling">
        <OverflowMenuLink to={`${root}/${item.id}?edit=throttling`}>Control incoming chats</OverflowMenuLink>
      </OverflowMenuItem>

      {renderConfigureChat(root, item, isMobileSDKAvailable)}
      {renderPreviewLink(item)}

      <OverflowMenuItem id="embed">
        <OverflowMenuLink
          onClick={() => {
            analytics.track('Admin - Sidekick Embed Viewed', { sidekickId: item.address });
            onClose();
            openEmbedModal(item);
          }}
          to="#"
        >
          Embed
        </OverflowMenuLink>
      </OverflowMenuItem>
    </OverflowMenu>
  );
};

const StyledEmbedModal = styled(EmbedModal)`
  .modalCard-content {
    padding: 20px 100px;
  }
`;

export default ChatConfigurations;
